// src/modules/treasury/pages/BranchExpensesPage.jsx
// Branch-facing petty-cash expenses: a branch logs an operating expense and pays
// it straight out of its own cash drawer (BranchCashLedger CASH_OUT, module EXPENSE).
// Branch users are locked to their own branch; HO users can pick any branch.
// Records the same CompanyExpense used by Accounting → Expense, so everything rolls
// up into the Branch-wise Expense Report.

import { isGlobalViewer } from '../../../constants/roles';
import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Table, Button, Typography, Tag, Space, Modal, Form,
  Input, InputNumber, DatePicker, Select, message, Popconfirm, Alert, Divider, Spin, Empty, Tooltip, Switch,
} from 'antd';
import {
  WalletOutlined, PlusOutlined, ReloadOutlined, ShopOutlined, DeleteOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById, fetchBranches } from '../../branches/api';
import { fetchCashLedgerBalance } from '../api';
import { fetchExpenses, createExpense, payExpense, deleteExpense } from '../../accounting/api';
import DenominationCountForm from '../components/DenominationCountForm';

const { Title, Text } = Typography;
const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

// Cash / petty-cash categories a branch actually spends on (salary/depreciation are HO-only).
const BRANCH_CATEGORIES = [
  { value: 'RENT', label: 'Rent' },
  { value: 'ELECTRICITY', label: 'Electricity' },
  { value: 'INTERNET_TELECOM', label: 'Internet & Telecom' },
  { value: 'OFFICE_SUPPLIES', label: 'Office Supplies' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'PACKAGING_LOGISTICS', label: 'Packaging & Logistics' },
  { value: 'SECURITY', label: 'Security' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'MARKETING_OFFLINE', label: 'Marketing (Offline)' },
  { value: 'MISCELLANEOUS', label: 'Miscellaneous' },
];
const CATEGORY_MAP = Object.fromEntries(BRANCH_CATEGORIES.map((c) => [c.value, c.label]));

const STATUS_COLORS = { PENDING: 'orange', APPROVED: 'blue', PAID: 'green', REJECTED: 'red', REVERSED: 'default' };

export default function BranchExpensesPage() {
  const { user } = useAuth();

  const [userBranch, setUserBranch] = useState(null);
  const [isHO, setIsHO] = useState(false);
  const [branches, setBranches] = useState([]);
  const [initLoading, setInitLoading] = useState(true);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const [balance, setBalance] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // Add / pay modal
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [denomCounts, setDenomCounts] = useState({});
  // Pay-existing modal
  const [payTarget, setPayTarget] = useState(null);
  const [payDate, setPayDate] = useState(dayjs());

  const branchId = selectedBranchId || (isHO ? null : user?.branch);

  // ── Init: resolve branch type ──
  useEffect(() => {
    (async () => {
      try {
        if (!user?.branch) { setInitLoading(false); return; }
        const branchRes = await fetchBranchById(user.branch);
        const branch = branchRes?.data || branchRes;
        setUserBranch(branch);
        const ho = branch?.branchType === 'HO' || isGlobalViewer(user);
        setIsHO(ho);
        if (ho) {
          const bRes = await fetchBranches({ limit: 1000 });
          setBranches(bRes?.data?.data || bRes?.data || []);
        } else {
          setSelectedBranchId(user.branch);
        }
      } catch {
        messageApi.error('Failed to load branch profile');
      } finally {
        setInitLoading(false);
      }
    })();
  }, [user, messageApi]);

  const load = useCallback(async () => {
    if (!branchId) { setBalance(null); setData([]); return; }
    setLoading(true);
    try {
      const [balRes, expRes] = await Promise.all([
        fetchCashLedgerBalance({ branchId }),
        fetchExpenses({ branch: branchId, limit: 100 }),
      ]);
      setBalance(balRes?.data?.data || null);
      setData(expRes.data || []);
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to load branch expenses');
    } finally {
      setLoading(false);
    }
  }, [branchId, messageApi]);

  useEffect(() => { if (!initLoading) load(); }, [initLoading, load]);

  const drawerBalance = balance?.runningBalance ?? balance?.summary?.closingBalance ?? 0;

  const openAdd = () => {
    form.resetFields();
    form.setFieldsValue({ expenseDate: dayjs(), payNow: true });
    setDenomCounts({});
    setModalOpen(true);
  };

  const denomTotal = Object.entries(denomCounts).reduce((s, [k, n]) => {
    const val = Number(String(k).replace('note', '')) || 0;
    return s + val * (Number(n) || 0);
  }, 0);

  const handleAdd = async (values) => {
    if (!branchId) { messageApi.error('Select a branch first'); return; }
    const amount = Number(values.amount) || 0;
    const payNow = values.payNow;
    const hasDenoms = denomTotal > 0;

    if (payNow && hasDenoms && Math.round(denomTotal) !== Math.round(amount)) {
      messageApi.error(`Denomination total (${fmt(denomTotal)}) must equal the amount (${fmt(amount)})`);
      return;
    }
    if (payNow && drawerBalance < amount) {
      messageApi.error(`Insufficient drawer cash (${fmt(drawerBalance)}). Fund the branch before paying in cash.`);
      return;
    }

    setSubmitting(true);
    try {
      const created = await createExpense({
        branch: branchId,
        category: values.category,
        description: values.description,
        amount,
        taxAmount: 0,
        expenseDate: values.expenseDate?.toISOString(),
        payeeName: values.payeeName,
        paymentMode: 'CASH',
        status: 'APPROVED',
        remarks: values.remarks,
      });
      const expenseId = created?.data?._id;

      if (payNow && expenseId) {
        await payExpense(expenseId, {
          source: 'DRAWER',
          businessDate: values.expenseDate?.toISOString(),
          ...(hasDenoms ? { denominations: denomCounts } : {}),
          remarks: values.remarks,
        });
        messageApi.success('Expense recorded and paid from drawer');
      } else {
        messageApi.success('Expense recorded (unpaid)');
      }
      setModalOpen(false);
      form.resetFields();
      setDenomCounts({});
      load();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to record expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayExisting = async () => {
    if (!payTarget) return;
    setSubmitting(true);
    try {
      await payExpense(payTarget._id, {
        source: 'DRAWER',
        businessDate: payDate?.toISOString(),
        ...(denomTotal > 0 ? { denominations: denomCounts } : {}),
      });
      messageApi.success('Expense paid from drawer');
      setPayTarget(null);
      setDenomCounts({});
      load();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to pay expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReverse = async (record) => {
    try {
      const res = await deleteExpense(record._id);
      messageApi.success(res?.data?.status === 'REVERSED' ? 'Reversed — cash returned to drawer' : 'Deleted');
      load();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to reverse');
    }
  };

  const openPayExisting = (record) => {
    setPayTarget(record);
    setPayDate(dayjs());
    setDenomCounts({});
  };

  if (initLoading) return <Spin style={{ display: 'block', marginTop: 80 }} />;

  const branchName = isHO ? branches.find((b) => b._id === selectedBranchId)?.name : userBranch?.name;

  const columns = [
    { title: 'ID', dataIndex: 'expenseId', width: 110 },
    { title: 'Category', dataIndex: 'category', width: 150, render: (v) => <Tag>{CATEGORY_MAP[v] || v}</Tag> },
    { title: 'Description', dataIndex: 'description', ellipsis: true },
    { title: 'Amount', dataIndex: 'totalAmount', width: 120, align: 'right', render: (v) => <Text strong>{fmt(v)}</Text> },
    { title: 'Payee', dataIndex: 'payeeName', width: 130, render: (v) => v || '—' },
    {
      title: 'Paid Via', dataIndex: 'paidVia', width: 110,
      render: (v) => v === 'DRAWER' ? <Tag color="purple">Drawer</Tag> : v === 'FUND' ? <Tag color="blue">Fund</Tag> : <Text type="secondary">—</Text>,
    },
    { title: 'Status', dataIndex: 'status', width: 100, render: (v) => <Tag color={STATUS_COLORS[v]}>{v}</Tag> },
    { title: 'Date', dataIndex: 'expenseDate', width: 100, render: (v) => v ? dayjs(v).format('DD/MM/YY') : '—' },
    {
      title: 'Actions', key: 'actions', fixed: 'right', width: 130,
      render: (_, r) => (
        <Space size="small">
          {r.status === 'APPROVED' && (
            <Tooltip title="Pay from drawer">
              <Button size="small" type="primary" ghost icon={<CreditCardOutlined />} onClick={() => openPayExisting(r)} />
            </Tooltip>
          )}
          {r.status !== 'REVERSED' && (
            <Popconfirm
              title={r.status === 'PAID' ? 'Reverse this expense?' : 'Delete this expense?'}
              description={r.status === 'PAID' ? 'Cash will be returned to the branch drawer.' : undefined}
              okText={r.status === 'PAID' ? 'Reverse' : 'Delete'}
              okButtonProps={{ danger: true }}
              onConfirm={() => handleReverse(r)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 0' }}>
      {contextHolder}

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            <WalletOutlined style={{ marginRight: 8 }} />
            Branch Expenses
          </Title>
          {branchName && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <ShopOutlined style={{ marginRight: 4 }} />{branchName}
            </Text>
          )}
        </Col>
        <Col>
          <Space wrap>
            {isHO && (
              <Select
                placeholder="Select branch" allowClear showSearch style={{ width: 220 }}
                value={selectedBranchId} onChange={setSelectedBranchId} optionFilterProp="label"
                options={branches.map((b) => ({ value: b._id, label: `${b.name} (${b.code})` }))}
              />
            )}
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd} disabled={!branchId}>
              Add Expense
            </Button>
          </Space>
        </Col>
      </Row>

      {isHO && !branchId ? (
        <Card><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a branch to log expenses" /></Card>
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ background: 'linear-gradient(135deg,#f9f0ff 0%,#f0f5ff 100%)' }}>
                <Statistic
                  title={<span><WalletOutlined /> Drawer Cash Available</span>}
                  value={fmt(drawerBalance)}
                  valueStyle={{ fontSize: 22, fontWeight: 'bold', color: drawerBalance < 0 ? '#cf1322' : '#531dab' }}
                />
              </Card>
            </Col>
          </Row>

          <Table
            dataSource={data} rowKey="_id" columns={columns}
            loading={loading} size="small" scroll={{ x: 1050 }}
            pagination={data.length > 20 ? { pageSize: 20, showSizeChanger: true } : false}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No branch expenses yet" /> }}
          />
        </>
      )}

      {/* ── Add Expense modal ── */}
      <Modal
        title="Add Branch Expense"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={submitting}
        okText="Save"
        width={620}
      >
        <Form form={form} layout="vertical" onFinish={handleAdd}>
          <Alert
            type={drawerBalance < 0 ? 'error' : 'info'} showIcon style={{ marginBottom: 12 }}
            message={`Drawer cash available: ${fmt(drawerBalance)}`}
          />
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Category" name="category" rules={[{ required: true }]}>
                <Select options={BRANCH_CATEGORIES} placeholder="Select category" showSearch optionFilterProp="label" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Amount (₹)" name="amount" rules={[{ required: true }]}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Description" name="description" rules={[{ required: true }]}>
            <Input placeholder="What was this spent on?" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Payee" name="payeeName">
                <Input placeholder="Vendor / person paid" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Date" name="expenseDate" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Pay from drawer now" name="payNow" valuePropName="checked" tooltip="Debits the branch cash drawer immediately. Turn off to just record it as unpaid.">
            <Switch checkedChildren="Pay in cash now" unCheckedChildren="Record as unpaid" />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(p, c) => p.payNow !== c.payNow || p.amount !== c.amount}>
            {({ getFieldValue }) => getFieldValue('payNow') ? (
              <>
                <Divider plain style={{ margin: '4px 0 12px' }}>
                  Denomination breakdown (optional — must equal {fmt(getFieldValue('amount') || 0)})
                </Divider>
                <DenominationCountForm
                  counts={denomCounts}
                  onChange={setDenomCounts}
                  expectedCash={getFieldValue('amount') || 0}
                />
              </>
            ) : null}
          </Form.Item>

          <Form.Item label="Remarks" name="remarks" style={{ marginTop: 12 }}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Pay existing (APPROVED) expense from drawer ── */}
      <Modal
        title={`Pay from Drawer — ${payTarget?.expenseId || ''}`}
        open={!!payTarget}
        onCancel={() => setPayTarget(null)}
        onOk={handlePayExisting}
        confirmLoading={submitting}
        okText="Pay"
        width={600}
      >
        {payTarget && (
          <>
            <Alert
              type={drawerBalance < (payTarget.totalAmount || 0) ? 'error' : 'info'} showIcon style={{ marginBottom: 12 }}
              message={`Amount ${fmt(payTarget.totalAmount)} · Drawer available ${fmt(drawerBalance)}`}
              description={drawerBalance < (payTarget.totalAmount || 0) ? 'Insufficient drawer cash — fund the branch first.' : undefined}
            />
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">Business day: </Text>
              <DatePicker value={payDate} onChange={setPayDate} style={{ width: 200 }} />
            </div>
            <Divider plain style={{ margin: '4px 0 12px' }}>
              Denomination breakdown (optional — must equal {fmt(payTarget.totalAmount || 0)})
            </Divider>
            <DenominationCountForm
              counts={denomCounts}
              onChange={setDenomCounts}
              expectedCash={payTarget.totalAmount || 0}
            />
          </>
        )}
      </Modal>
    </div>
  );
}
